import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Hammer,
  Stethoscope,
  Wrench,
  Store,
  Scissors,
  Building2,
  Star,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';
import { listReviews, reviewSlug, type Review } from '../api';

/**
 * CustomersPage
 * --------------------------------------------------------------
 * Customers / case studies index. Backed by /api/public/site/reviews:
 * super-admins manage the list from the admin dashboard, the marketing
 * site fetches and renders them here.
 *
 * Fallback: when the API returns no reviews, render six honest sector
 * placeholders so the page never looks empty (and never invents customer
 * names).
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const SECTOR_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  studio: Hammer,
  clinic: Stethoscope,
  trades: Wrench,
  shop:   Store,
  retail: Store,
  salon:  Scissors,
  agency: Building2,
};

function iconForSector(sector: string | null | undefined): React.ComponentType<{ className?: string }> {
  if (!sector) return Building2;
  const s = sector.toLowerCase();
  for (const key of Object.keys(SECTOR_ICONS)) {
    if (s.includes(key)) return SECTOR_ICONS[key];
  }
  return Building2;
}

/* ─────────────────────────────────────────────────────────────
 * Shared review primitives — exported so LandingPage (and any
 * future testimonial surface) can render the same Avatar + Stars
 * treatment without duplicating styles.
 * ───────────────────────────────────────────────────────────── */

export function Stars({ rating, className }: { rating: number; className?: string }) {
  return (
    <span className={'inline-flex items-center gap-0.5 ' + (className ?? '')}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={
            'h-3.5 w-3.5 ' +
            (i < rating ? 'fill-coral text-coral' : 'text-ink/15 dark:text-white/15')
          }
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

export function Avatar({
  name,
  url,
  size = 'md',
  className,
}: {
  name: string;
  url?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizes = {
    sm: 'h-9 w-9 text-[12px]',
    md: 'h-11 w-11 text-[14px]',
    lg: 'h-14 w-14 text-[18px]',
  } as const;
  const ring = 'ring-1 ring-ink/8 dark:ring-white/10';
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={['rounded-full object-cover', sizes[size], ring, className ?? ''].join(' ')}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={[
        'rounded-full bg-coral text-white flex items-center justify-center font-bold',
        sizes[size],
        ring,
        className ?? '',
      ].join(' ')}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

type Placeholder = {
  slug: string;
  sector: string;
  Icon: React.ComponentType<{ className?: string }>;
  city: string;
  size: string;
  modules: string[];
};

const PLACEHOLDERS: Placeholder[] = [
  { slug: 'studio',   sector: 'Design studio',     Icon: Hammer,      city: 'Portland, OR',     size: '8 people',  modules: ['Invoicing', 'Projects', 'Time']  },
  { slug: 'clinic',   sector: 'Dental clinic',     Icon: Stethoscope, city: 'Austin, TX',       size: '12 people', modules: ['HR', 'Payroll', 'Expenses']      },
  { slug: 'trades',   sector: 'Plumbing & trades', Icon: Wrench,      city: 'Manchester, UK',   size: '22 people', modules: ['Invoicing', 'Payroll', 'Time']   },
  { slug: 'shop',     sector: 'Specialty retail',  Icon: Store,       city: 'Brooklyn, NY',     size: '6 people',  modules: ['Invoicing', 'Expenses', 'HR']    },
  { slug: 'salon',    sector: 'Hair & beauty',     Icon: Scissors,    city: 'Madrid, ES',       size: '5 people',  modules: ['Invoicing', 'Payroll', 'HR']     },
  { slug: 'agency',   sector: 'Creative agency',   Icon: Building2,   city: 'Toronto, CA',      size: '14 people', modules: ['Invoicing', 'Projects', 'Time']  },
];

export function CustomersPage() {
  const [reviews, setReviews] = React.useState<Review[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listReviews({ sort: 'default' })
      .then((rs) => { if (!cancelled) setReviews(rs); })
      .catch(() => { if (!cancelled) setReviews([]); });
    return () => { cancelled = true; };
  }, []);

  const hasReal = reviews !== null && reviews.length > 0;

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Customers"
        description="Operators running their day-to-day on Worqly — design studios, clinics, trades, and creative agencies."
      />
      <Hero hasReal={hasReal} />
      {hasReal ? <ReviewGrid reviews={reviews!} /> : <PlaceholderGrid />}
      <FirstCustomers />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HERO
 * ───────────────────────────────────────────────────────────── */

function Hero({ hasReal }: { hasReal: boolean }) {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Sparkles className="h-3 w-3 text-coral" />
              Customer stories
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            Operators running on{' '}
            <span className="italic font-serif text-coral">Worqly.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            {hasReal
              ? 'Real teams using Worqly to invoice, pay, hire, and ship. Their words, their numbers, their workflows.'
              : 'We\'re a young product. The teams below have committed to letting us tell their story — real names, real numbers, real workflows — as soon as the quote is approved.'}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * REAL REVIEWS — when the API returns rows
 * ───────────────────────────────────────────────────────────── */

function ReviewGrid({ reviews }: { reviews: Review[] }) {
  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
        >
          {reviews.map((r) => {
            const Icon = iconForSector(r.sector);
            const slug = reviewSlug(r);
            return (
              <motion.li key={r.id} variants={FADE_UP}>
                <Link
                  to={`/customers/${slug}`}
                  className="group block h-full rounded-3xl p-6 transition-all bg-white/85 ring-1 ring-ink/5 shadow-card hover:shadow-card-lg dark:bg-white/[0.04] dark:ring-white/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 group-hover:bg-coral group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    {r.rating != null && (
                      <span className="inline-flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={
                              'h-3.5 w-3.5 ' +
                              (i < (r.rating ?? 0)
                                ? 'fill-coral text-coral'
                                : 'text-ink/15 dark:text-white/15')
                            }
                            strokeWidth={1.5}
                          />
                        ))}
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-[18px] font-semibold text-ink dark:text-white tracking-[-0.01em] truncate">
                    {r.company || r.name}
                  </h3>
                  <p className="mt-1 text-[12.5px] text-ink/55 dark:text-white/55 truncate">
                    {[r.sector, r.city].filter(Boolean).join(' · ') || 'Operator'}
                  </p>

                  <blockquote className="mt-4 text-[13.5px] text-ink/75 dark:text-white/75 leading-relaxed line-clamp-5">
                    "{r.body}"
                  </blockquote>

                  <p className="mt-5 text-[12px] text-ink/55 dark:text-white/55">
                    — {r.name}{r.role ? `, ${r.role}` : ''}
                  </p>

                  <div className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-coral group-hover:gap-2 transition-all">
                    Read the story
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * PLACEHOLDER GRID — when the API is empty (no fabricated names)
 * ───────────────────────────────────────────────────────────── */

function PlaceholderGrid() {
  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
        >
          {PLACEHOLDERS.map((p) => (
            <motion.li key={p.slug} variants={FADE_UP}>
              <Link
                to={`/customers/${p.slug}`}
                className="group block h-full rounded-3xl p-6 transition-all bg-white/85 ring-1 ring-ink/5 shadow-card hover:shadow-card-lg dark:bg-white/[0.04] dark:ring-white/10"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 group-hover:bg-coral group-hover:text-white transition-colors">
                    <p.Icon className="h-5 w-5" />
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-ink/5 dark:bg-white/10 text-[10.5px] font-bold uppercase tracking-[0.10em] text-ink/55 dark:text-white/55">
                    Awaiting approval
                  </span>
                </div>

                <h3 className="mt-5 text-[18px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                  {p.sector}
                </h3>
                <p className="mt-1 text-[12.5px] text-ink/55 dark:text-white/55">
                  {p.city} · {p.size}
                </p>

                <blockquote className="mt-4 text-[13.5px] text-ink/70 dark:text-white/70 leading-relaxed italic">
                  "Quote awaiting customer approval. The numbers — hours saved, invoices sent, payroll
                  runs — will be filled in from real workspace data."
                </blockquote>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {p.modules.map((m) => (
                    <span
                      key={m}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-pearl-100 dark:bg-white/[0.06] text-[11px] font-semibold text-ink/65 dark:text-white/65"
                    >
                      {m}
                    </span>
                  ))}
                </div>

                <div className="mt-5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-coral group-hover:gap-2 transition-all">
                  Story coming soon
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FIRST CUSTOMERS — recruit CTA
 * ───────────────────────────────────────────────────────────── */

function FirstCustomers() {
  return (
    <section className="py-20 lg:py-24 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="rounded-3xl bg-coral-gradient text-white shadow-card-lg ring-1 ring-white/10 overflow-hidden p-8 lg:p-12 relative"
        >
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div variants={FADE_UP} className="lg:col-span-7">
              <Eyebrow className="!text-white/80">Be one of our first</Eyebrow>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Trade your story for a{' '}
                <em className="font-serif">year of Worqly.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                The first ten customers who let us publish a real case study get 12 months free on
                every module their workspace uses. Honest numbers, your name above the door, no
                obligation to overstate the wins.
              </p>
            </motion.div>
            <motion.div variants={FADE_UP} className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <a
                href="mailto:hello@worqly.io?subject=Tell%20our%20story"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Email hello@worqly.io
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                Start free first
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Footer CTA
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-pearl-100 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card-lg p-10 lg:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-coral">Start your own story</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance text-ink dark:text-white">
                Try Worqly. <em className="font-serif italic text-coral">Then write the case study.</em>
              </h2>
              <p className="mt-4 text-body-lg text-ink/70 dark:text-white/70 max-w-xl">
                Seven days free, no card to start. If we save you a Friday a month, we'd love to
                tell that story together.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
